import { useCallback, useEffect, useRef } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "@/shared/api/client";
import { authApi } from "@/shared/api/auth";
import { useAuth } from "@/shared/auth/store";
import { currentLanguage, type Language } from "@/shared/i18n";
import {
  ALERT_LANGUAGES,
  buildAlertPhrase,
  buildClearedAlert,
  buildSpokenAlert,
  isSilentHealth,
  SPEECH_LOCALE,
  toAlertLang,
  type AlertLang,
  type Level,
} from "@/shared/i18n/alertPhrases";
import { isNativeTTS, nativeSpeak, nativeStopSpeaking } from "@/shared/native/tts";
import { useAlarmSettings } from "@/shared/hooks/useAlarmSettings";
import { playAlarmTone } from "@/shared/lib/alarmTone";
import { playCautionChime } from "@/shared/lib/cautionChime";

/**
 * useAlertTTS — spoken alert announcements in 8 languages (협약 6종
 * ko · en · vi · mn · zh · ja + 추가 2종 th · id) via the Web Speech API,
 * with a server-side edge-tts fallback.
 *
 * Playback strategy (per the P4-B spec):
 *   1. Web Speech API (`speechSynthesis`) using a locally-installed voice for
 *      the target language.
 *   2. If no local voice exists for that language (common for vi/th/id on
 *      desktop, and locked-down kiosks) → fall back to the backend
 *      `GET /api/v1/tts/speak` proxy (edge-tts → cached MP3). If that is also
 *      unavailable (503), the alarm degrades to visual-only — never crashes.
 *
 * 네이티브(Capacitor 안드로이드/iOS)에서는 **1단계가 OS TTS 엔진**으로 바뀐다:
 * 안드로이드 WebView 의 `speechSynthesis.getVoices()` 가 빈 배열이라 Web Speech 로는
 * 로컬 음성을 하나도 찾지 못하고, 결국 모든 언어가 서버 edge-tts 를 타야만 소리가
 * 난다 — 데이터가 끊기면 음성 경보가 통째로 무음이 된다. 그래서 네이티브에서는
 *   네이티브 TTS(`@capacitor-community/text-to-speech`) → 서버 edge-tts MP3
 * 순서로 발화한다(`shared/native/tts.ts`). 엔진에 언어팩이 없는 언어는 억지로
 * 넘기지 않고 곧장 서버 폴백으로 보낸다. **웹 경로는 그대로다.**
 *
 * Language: follows the app UI language (`currentLanguage()`) unless the user
 * sets an explicit voice override (`langOverride`). Both the passive announcer
 * (AppShell) and the critical-alarm overlay (AlarmCenter) read this hook.
 *
 * Broadcast languages: automatic alarm announcements (`announce`/`announceOne`)
 * are spoken in **every language the operator selected in Settings**
 * (`broadcastLangs`, default `ko`), one after another — see `broadcastPhrases`.
 * The set is independent of the on-screen display language (a wall board may
 * cycle the full 협약 kiosk set while its UI stays Korean), **but the primary
 * voice language (`voiceLang`) follows the display language and is spoken
 * first** (2026-09-11): a worker who switched the app to Tiếng Việt heard the
 * alarm in Korean only — the display and the voice looked like one switch on the
 * phone, and they were not. LanguageMenu now calls `followDisplayLanguage`: a
 * single-language set is *replaced* (the phone case), a deliberately multi-
 * language set keeps its members and gains the new primary (the board case);
 * either way `voiceLang` syncs to the server profile. The imperative `speak()`
 * replay and the display-facing `lang` field are unchanged.
 *
 * Silent-failure policy (2026-08-31): a broadcast that never reaches a speaker
 * must NEVER look like a successful one. Every failing leg — a Web Speech
 * `onerror`, a missing local voice with the server fallback off, an edge-tts
 * request/playback failure — records `lastFailure` on the store and warns on the
 * console, and any successful utterance clears the record for that language.
 * The TopBar voice button renders that state so "6개국어 방송했다" and
 * "2개만 나갔다" can no longer be confused.
 *
 * Dedupe: every alert id is spoken at most once for the tab lifetime, tracked
 * in a module-level set shared across all mounts so the passive announcer and
 * the overlay never double-speak.
 */

const supported =
  typeof window !== "undefined" && "speechSynthesis" in window;

interface TtsState {
  /** Master voice-alarm switch (TopBar / NOC / overlay). Default off. */
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  /** Allow the server edge-tts fallback when no local voice exists. */
  serverFallback: boolean;
  setServerFallback: (v: boolean) => void;
  /**
   * 브라우저에서 **서버 신경망 음성(edge-tts)을 1순위**로 쓴다. 기본 켬.
   *
   * 2026-09-15 8개국어 순차 테스트: 영어가 쉰 목소리(윈도우 SAPI 구형 음성이 첫 후보), 언어마다
   * 음색·속도가 제각각이라 방송처럼 들리지 않았다. 서버 음성은 8개 언어가 같은 계열의 신경망
   * 음성이라 고르고, 어느 PC 에서 틀어도 같다. 서버가 안 되면 브라우저 음성으로 내려간다.
   * 네이티브 앱(안드로이드/iOS)은 OS 음성이 1순위 그대로(오프라인에서도 울려야 한다).
   */
  preferServer: boolean;
  setPreferServer: (v: boolean) => void;
  /** Explicit voice language; null = follow the app UI language. */
  langOverride: Language | null;
  setLangOverride: (v: Language | null) => void;
  /**
   * Languages an automatic alarm is spoken in, in order. Independent of the
   * display/UI language — the operator picks these in Settings from the 협약
   * kiosk set (ko · en · zh · vi · th · id). Empty is coerced to `["ko"]`.
   */
  broadcastLangs: AlertLang[];
  setBroadcastLangs: (v: AlertLang[]) => void;
  /**
   * 이 계정의 **주 음성 언어**(서버 `voice_lang` 와 동기화되는 단일 값).
   * 단말을 바꿔도 따라오도록 서버에 저장하고, 항상 `broadcastLangs` 에 포함된다.
   */
  voiceLang: AlertLang;
  setVoiceLang: (v: AlertLang) => void;
  /**
   * 표시 언어를 바꿀 때 음성이 따라가는 규칙(LanguageMenu 가 부른다).
   * 방송 언어가 **하나뿐**이면 그 하나를 새 언어로 **바꾼다** — 폰에서 언어를 몇 번
   * 눌러 본 것만으로 경보마다 4개국어가 줄줄이 나오면 안 된다. 설정에서 **여러 언어를
   * 일부러 골라 둔** 경우(보드의 다국어 순환)에는 집합을 지키고 주 언어만 더한다.
   */
  followDisplayLanguage: (v: AlertLang) => void;
  /**
   * 마지막으로 실패한 발화(언어·사유·시각). 성공하면 해당 언어 기록이 지워진다.
   * 세션 진단용이라 **persist 대상이 아니다**(아래 `partialize` 참조).
   */
  lastFailure: TtsFailure | null;
  noteFailure: (lang: AlertLang, reason: string) => void;
  clearFailure: (lang: AlertLang) => void;
}

/** 조용한 실패를 눈에 보이게 만드는 기록 한 건. */
export interface TtsFailure {
  lang: AlertLang;
  /** 기계 판독용 사유 코드 — `speech_error:not-allowed`, `server_tts_failed` 등. */
  reason: string;
  /** epoch ms. */
  at: number;
}

/** Persisted settings, shared across every consumer of the hook. */
export const useTtsStore = create<TtsState>()(
  persist(
    (set) => ({
      // 기본 **ON** (2026-09-03 사용자 지시: 「기본으로 경보는 켜」).
      //
      // 경위: v1 에서 기본 ON 으로 했다가 2026-08-31 사고로 v2 에서 강제로 껐다 —
      // AlertTtsAnnouncer 가 「목록을 받았다」와 「경보가 발령됐다」를 구분하지 못해
      // 화면을 열 때마다 며칠 지난 경보 12건을 줄줄이 낭독했다.
      // 그 뒤 세 겹으로 막았다: **세션 기준선**(첫 응답은 통째로 흡수하고 침묵) +
      // **틱당 최대 3건 상한** + **미해제만 낭독**(해제된 경보는 애초에 후보가 아니다).
      // 껐다 켜는 것을 사용자에게 맡겨 두면 **아무도 켜지 않은 콘솔은 처음부터 끝까지
      // 조용하다** — 가스 안전 제품에서 그건 기본값으로 둘 상태가 아니다.
      // 끄는 곳: 상단바 스피커 아이콘 · 설정 > 음성 경보 방송.
      enabled: true,
      setEnabled: (enabled) => set({ enabled }),
      serverFallback: true,
      setServerFallback: (serverFallback) => set({ serverFallback }),
      preferServer: true,
      setPreferServer: (preferServer) => set({ preferServer }),
      langOverride: null,
      setLangOverride: (langOverride) => set({ langOverride }),
      // Default: Korean only. Operators opt into extra spoken languages in
      // Settings; order is normalised to the canonical ALERT_LANGUAGES order.
      broadcastLangs: ["ko"],
      setBroadcastLangs: (langs) =>
        set({
          broadcastLangs: ALERT_LANGUAGES.filter((l) => langs.includes(l)) as AlertLang[],
        }),
      voiceLang: "ko",
      setVoiceLang: (voiceLang) =>
        set((s) => ({
          voiceLang,
          // 주 언어는 반드시 실제로 방송되는 언어여야 한다 — 빠져 있으면 넣는다.
          broadcastLangs: s.broadcastLangs.includes(voiceLang)
            ? s.broadcastLangs
            : (ALERT_LANGUAGES.filter(
                (l) => l === voiceLang || s.broadcastLangs.includes(l),
              ) as AlertLang[]),
        })),
      followDisplayLanguage: (voiceLang) =>
        set((s) => ({
          voiceLang,
          broadcastLangs:
            s.broadcastLangs.length <= 1
              ? [voiceLang]
              : (ALERT_LANGUAGES.filter(
                  (l) => l === voiceLang || s.broadcastLangs.includes(l),
                ) as AlertLang[]),
        })),
      lastFailure: null,
      noteFailure: (lang, reason) => {
        // 조용히 삼키지 않는다 — 콘솔과 상단바 양쪽에 남긴다.
        console.warn(`[tts] 음성 방송 실패 — ${lang}: ${reason}`);
        set({ lastFailure: { lang, reason, at: Date.now() } });
      },
      clearFailure: (lang) =>
        set((s) => (s.lastFailure?.lang === lang ? { lastFailure: null } : s)),
    }),
    {
      name: "expertgas.tts.v4",
      // v2 = v1(기본 ON 전환)의 철회 — 각 브라우저에 enabled:false 를 써 버렸다.
      // v3 = 그 철회의 재철회(2026-09-03 지시). **저장값을 그대로 두면 v2 가 꺼 놓은
      //      기존 콘솔은 영원히 조용하다** — 기본값만 바꿔서는 현장에 반영되지 않는다.
      //      그래서 한 번 강제로 켠다(이후 사용자가 끄면 그 값이 유지된다).
      //      v2 때와 대칭이며, 되살아난 낭독은 위 세 겹(기준선·상한·미해제)이 막는다.
      version: 3,
      migrate: (state: unknown, from: number) => {
        const s = (state ?? {}) as Record<string, unknown>;
        if (from < 2) return { ...s, enabled: false };
        return from < 3 ? { ...s, enabled: true } : s;
      },
      // `lastFailure` 는 이번 세션의 진단값이라 저장하지 않는다(오래된 빨간 표시 방지).
      partialize: (s) => ({
        enabled: s.enabled,
        serverFallback: s.serverFallback,
        preferServer: s.preferServer,
        langOverride: s.langOverride,
        broadcastLangs: s.broadcastLangs,
        voiceLang: s.voiceLang,
      }),
    },
  ),
);

/** 훅 밖(큐 드레이너 등)에서 실패/성공을 기록하기 위한 얇은 래퍼. */
function noteFailure(lang: AlertLang, reason: string) {
  useTtsStore.getState().noteFailure(lang, reason);
}
function noteSuccess(lang: AlertLang) {
  useTtsStore.getState().clearFailure(lang);
}

// Alert ids already spoken — shared across all hook instances (passive
// announcer + overlay) so a level-3 alert handled by both never double-speaks.
const spoken = new Set<number>();

/**
 * 주의(1단계) 경보 알림음(2026-10-19, 10-12 검증 V-31). 낭독(경계 이상)과 달리 음성 없이 짧은 두 음만 낸다.
 * 설정 > 주의 알림음(`cautionChime`, **기본 끔**)과 경보음(`sound`)이 둘 다 켜져 있을 때만 울린다 —
 * 음성 끔(`enabled`)과는 무관하다(경보음과 같은 규칙). 중복 방지는 호출자가 `spoken` 으로 이미 했다.
 */
function chimeCaution(): void {
  const s = useAlarmSettings.getState();
  if (!s.cautionChime || !s.sound) return;
  playCautionChime();
}

/**
 * 주의 알림음 대상인가 — 주의(1단계) **가스** 경보만(2026-10-20, 3차 재통합 B1b×F1).
 * 설비 경보(kind=health)는 뺀다: 주의 등급 설비 경보는 센서 판독 모순(K, 서버 sensor_health.py — 「sensor:{node}:{gas}」,
 * SENSOR_HEALTH_K_ALERT 기본 끔)이 여는 유지보수 알림이라, 주의 알림음을 켠 관제실에서 가스 주의와 같은 소리를 내면 안 된다.
 * (무응답 설비 경보는 기본 심각(3)이라 애초에 낭독 경로 — 가스 사이렌 없이 안내 음성만, speakAlert.)
 */
function chimesFor(a: TtsAlertLike): boolean {
  return a.level === 1 && a.kind !== "health";
}

/** Minimal shape we need off an alert/alert-payload to announce it. */
export interface TtsAlertLike {
  id: number;
  level: number;
  site?: number | string | null;
  gas?: string | null;
  /** 서버 Alert.kind — "health"(설비 무응답)면 대피 지시 없는 설비 경고 문장, 경보음(사이렌) 없음. */
  kind?: string | null | undefined;
  /** 서버 Alert.is_drill — 참이면 「훈련 상황입니다」를 붙인다. */
  is_drill?: boolean | null | undefined;
  /** 서버 Alert.subject_key — 무응답 대상("manager:16" 등). */
  subject_key?: string | null | undefined;
  /** 같은 문장으로 묶어 한 번 읽는 경보 전부(대표 포함). 이 중 **하나라도** 열려 있으면 낭독을 끊지 않는다. */
  groupIds?: number[] | undefined;
}

// ── Voice selection (Web Speech) ─────────────────────────────────────────────
let voiceCache: SpeechSynthesisVoice[] = [];
function refreshVoices() {
  if (!supported) return;
  try {
    voiceCache = window.speechSynthesis.getVoices() ?? [];
  } catch {
    voiceCache = [];
  }
}
if (supported) {
  refreshVoices();
  try {
    window.speechSynthesis.onvoiceschanged = refreshVoices;
  } catch {
    /* some engines don't fire this — first getVoices() is enough */
  }
}

/**
 * 같은 언어의 음성이 여럿이면 **자연스러운 쪽**을 고른다. 윈도우 크롬은 SAPI 구형(David·Zira 등,
 * 쉰 목소리)이 목록 앞에 오고 그 뒤에 Google·Natural 음성이 있다 — 첫 번째를 집으면 늘 구형이 걸린다.
 */
function voiceScore(v: SpeechSynthesisVoice): number {
  const n = (v.name ?? "").toLowerCase();
  let score = 0;
  if (/natural|neural|online|premium|enhanced|wavenet/.test(n)) score += 4;
  if (/google/.test(n)) score += 3;
  if (/siri|samantha|yuna|kyoko|o-ren|nanami|sunhi|xiaoxiao/.test(n)) score += 2;
  if (/david|zira|mark|heami|haruka|huihui|compact|espeak/.test(n)) score -= 3;
  if (!v.localService) score += 1;                       // 원격(클라우드) 음성이 대체로 낫다
  return score;
}

function pickVoice(lang: AlertLang): SpeechSynthesisVoice | undefined {
  const base = lang.toLowerCase();
  const locale = SPEECH_LOCALE[lang].toLowerCase();
  const exact = voiceCache.filter((v) => v.lang?.toLowerCase().replace("_", "-") === locale);
  const loose = voiceCache.filter((v) => v.lang?.toLowerCase().startsWith(base));
  const pool = exact.length ? exact : loose;
  return [...pool].sort((a, b) => voiceScore(b) - voiceScore(a))[0];
}

// ── Server edge-tts fallback ─────────────────────────────────────────────────
let fallbackAudio: HTMLAudioElement | null = null;

/** 사유 문자열 — 예외 객체에서 사람이 읽을 한 줄만 뽑는다. */
function errText(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  return "unknown";
}

/**
 * 서버 edge-tts 로 한 문장을 읽고 **재생이 끝날 때까지** 기다린다. 성공하면 true.
 *
 * ⛔ 예전엔 `audio.play()` 만 기다렸다 — 그건 「재생 시작」이지 끝이 아니라, 서버 음성으로
 * 넘어간 언어(인도네시아어)가 아직 나오는 중에 다음 언어(일본어)가 시작돼 둘이 겹쳐 들렸다
 * (2026-09-15 8개국어 순차 테스트). 실패는 기록하되 던지지 않는다.
 */
async function serverSpeak(text: string, lang: AlertLang): Promise<boolean> {
  try {
    const res = await api.get("/tts/speak", {
      params: { text, lang },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data as Blob);
    stopFallbackAudio();
    const audio = new Audio(url);
    fallbackAudio = audio;
    return await new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (ok: boolean, reason?: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(guard);
        URL.revokeObjectURL(url);
        if (fallbackAudio === audio) fallbackAudio = null;
        if (ok) noteSuccess(lang);
        else noteFailure(lang, reason ?? "audio_failed");
        resolve(ok);
      };
      // 재생이 끝났다는 이벤트가 안 오는 브라우저를 위한 안전망 — 문장 길이에 비례.
      const guard = setTimeout(() => finish(true), Math.min(60_000, Math.max(8_000, text.length * 260 + 4_000)));
      audio.addEventListener("ended", () => finish(true), { once: true });
      audio.addEventListener("error", () => finish(false, "audio_error"), { once: true });
      audio.play().catch((e: unknown) => finish(false, `audio_play_failed:${errText(e)}`));
    });
  } catch (e) {
    // 503 tts_unavailable / network failure → visual-only alarm. Never throw —
    // 하지만 **조용히 넘어가지도 않는다**: 이 언어는 방송되지 않았다.
    noteFailure(lang, `server_tts_failed:${errText(e)}`);
    return false;
  }
}

/**
 * 서버 폴백 MP3만 멈춘다.
 *
 * ⛔ 여기서 네이티브 TTS 를 멈추면 안 된다. `serverSpeak()` 은 재생 **시작할 때마다** 이 함수를
 * 부르므로, 언어팩이 없어 서버로 넘어간 언어 하나가 **이미 읽고 있던 다른 언어들을 전부 끊는다**.
 * 게다가 플러그인의 `stop()` 은 콜백 맵을 비워 버려(TextToSpeech.java `requests.clear()`),
 * 끊긴 발화의 `speak()` Promise 가 **영영 resolve/reject 되지 않는다** — 성공도 실패도 기록되지 않고
 * 서버로 넘어간 언어만 「성공」으로 남아, 소리는 조각만 났는데 화면은 8개국어 전부 정상이 된다.
 * 사용자가 명시적으로 멈춘 경우(`stopBroadcast()` · 음성 끄기)에만 네이티브를 멈춘다.
 */
function stopFallbackAudio() {
  if (fallbackAudio) {
    try {
      fallbackAudio.pause();
    } catch {
      /* ignore */
    }
    fallbackAudio = null;
  }
}

/**
 * Speak `text` in `lang`: Web Speech if a local voice exists, else the server
 * edge-tts fallback (when allowed). Never throws.
 *
 * 네이티브에서는 Web Speech 대신 OS TTS 엔진이 1단계다(그 아래 폴백은 동일).
 */
function speakText(text: string, lang: AlertLang, serverFallback: boolean) {
  void speakTextAsync(text, lang, serverFallback);
}

/**
 * `speakText` 의 await 가능한 형태 — **발화가 끝날 때까지** 기다린다.
 * 순차 테스트처럼 「하나 끝나고 다음」이 필요한 곳에서 쓴다.
 * (경보 자동 발화는 별도의 FIFO 큐가 담당하므로 이 함수를 쓰지 않는다.)
 */
async function speakTextAsync(text: string, lang: AlertLang, serverFallback: boolean) {
  if (isNativeTTS()) {
    const r = await nativeSpeak(text, lang);
    if (r.status === "ok") {
      noteSuccess(lang);
      return;
    }
    // 실패는 조용히 삼키지 않는다. 언어팩이 없어 건너뛴 경우(unsupported)는
    // 서버가 대신 읽어 주면 방송이 나간 것이므로 아직 실패가 아니다.
    if (r.status === "error") noteFailure(lang, `native_tts_failed:${r.reason}`);
    if (serverFallback) {
      await serverSpeak(text, lang);
    } else if (r.status === "unsupported") {
      noteFailure(lang, "no_local_voice_and_fallback_off");
    }
    return;
  }
  // 브라우저: 서버 신경망 음성이 1순위(설정) — 되면 끝, 안 되면 브라우저 음성으로.
  if (serverFallback && useTtsStore.getState().preferServer) {
    if (await serverSpeak(text, lang)) return;
  }
  if (supported) {
    const voice = pickVoice(lang);
    // A voice exists (or none are enumerated yet → best-effort attempt).
    if (voice || voiceCache.length === 0) {
      try {
        const u = new SpeechSynthesisUtterance(text);
        u.lang = SPEECH_LOCALE[lang];
        if (voice) u.voice = voice;
        u.rate = 1;
        u.pitch = 1;
        await new Promise<void>((done) => {
          let settled = false;
          const finish = () => {
            if (!settled) { settled = true; done(); }
          };
          u.onend = () => { noteSuccess(lang); finish(); };
          u.onerror = (ev) => {
            const err = (ev as SpeechSynthesisErrorEvent).error ?? "unknown";
            // 사용자가 끄거나(mute) 다음 발화가 밀어낸 것은 실패가 아니다.
            if (err !== "interrupted" && err !== "canceled")
              noteFailure(lang, `speech_error:${err}`);
            finish();
          };
          // ⛔ 반드시 settle 시킨다 — onend/onerror 가 오지 않는 브라우저가 있다.
          setTimeout(finish, Math.min(60000, Math.max(8000, text.length * 260 + 4000)));
          window.speechSynthesis.speak(u);
        });
        return;
      } catch (e) {
        noteFailure(lang, `speech_throw:${errText(e)}`);
        /* fall through to server fallback */
      }
    }
    // Voices are loaded but none match this language → server fallback.
    if (serverFallback) await serverSpeak(text, lang);
    else noteFailure(lang, "no_local_voice_and_fallback_off");
    return;
  }
  // No Web Speech at all (older Safari, kiosk) → server fallback.
  if (serverFallback) await serverSpeak(text, lang);
  else noteFailure(lang, "no_speech_engine_and_fallback_off");
}

// ── Multilingual sequential broadcast ────────────────────────────────────────
// A single global FIFO queue speaks alerts in every selected language, strictly
// in order — across languages AND across a burst of alerts (no talk-over, no
// cut-off). Web Speech and the edge-tts fallback are unified behind a per-item
// promise so mixed engines (a local ko/en voice + server-rendered vi/th/id)
// still play one at a time.
//
// Deliberately self-contained: the single-shot `speakText`/`serverSpeak`/
// `stopFallbackAudio` paths and the disable-cancel effect above are left
// untouched, so the voice-stop/mute logic (owned by another surface) stays
// conflict-free. `stopBroadcast()` clears the queue and abandons the current
// item; `broadcastEpoch` invalidates in-flight work after a stop.
interface BroadcastItem {
  text: string;
  lang: AlertLang;
  serverFallback: boolean;
  /** 이 문장을 부른 경보 id — 그 경보가 전부 해제되면 아직 안 읽은 언어분을 큐에서 빼고, 읽는 중이면 끊는다. */
  alertIds?: number[] | undefined;
  /** 해제로 끊었다 — 뒤늦게 오는 성공·실패 콜백을 기록하지 않고 다음 엔진으로 넘어가지도 않는다. */
  cut?: boolean;
  /** 안전망 시한이 지나 큐가 다음으로 넘어갔다 — 늦게 도착한 음원은 틀지 않는다(다른 문장과 겹침·오기록 방지). */
  stale?: boolean;
  /** 지금 이 항목을 읽는 엔진 — 해제 중지는 이 엔진만 멈춘다(시험 낭독 등 다른 발화를 죽이지 않게, 2026-10-06 검토). */
  leg?: "web" | "native" | "audio";
  /** 이 항목이 틀고 있는 서버 음원 — 전역 broadcastAudio 대신 이것을 멈춘다. */
  audio?: HTMLAudioElement;
}
let broadcastQueue: BroadcastItem[] = [];
let broadcastPlaying = false;
let broadcastEpoch = 0;
let broadcastAudio: HTMLAudioElement | null = null;
/** 지금 읽고 있는 항목과 그 항목을 끝내는 함수 — 해제 중지가 이 한 문장만 끊는 데 쓴다. */
let currentItem: BroadcastItem | null = null;
let abortCurrent: (() => void) | null = null;
/** 서버가 실시간으로 알려 온 해제 경보 id(WS `alert.cleared`) — 경보음(약 3초) 뒤 낭독을 시작하기 전에도 본다. */
const clearedAlertIds = new Set<number>();

/** Clear the queue and abandon the current item. Queued Web Speech utterances
 *  are cancelled by the caller's `speechSynthesis.cancel()` (mute/disable). */
function stopBroadcast() {
  broadcastQueue = [];
  broadcastEpoch++;
  broadcastPlaying = false;
  currentItem = null;
  // 네이티브 엔진이 읽고 있던 문장도 끊는다(웹에서는 no-op).
  nativeStopSpeaking();
  if (broadcastAudio) {
    try {
      broadcastAudio.pause();
    } catch {
      /* ignore */
    }
    broadcastAudio = null;
  }
}

/**
 * 해제된 경보의 낭독을 거둔다 — 큐의 아직 안 읽은 언어분을 빼고, **지금 읽는 문장도 그 경보가 전부 해제됐으면
 * 끊는다**(2026-10-01: 현장 매니져는 해제 즉시 멈췄는데 웹·앱은 다국어 낭독을 계속했다. 09-28 에는 「읽는 한
 * 문장은 끝까지 둔다」였으나 「해제되면 방송 멈추게」로 정정).
 * 경보 id 가 없는 항목(시험 방송·해제 안내)과 아직 살아 있는 경보가 하나라도 걸린 항목은 그대로 — 에포크를 올리지
 * 않으므로 다른 경보의 낭독은 이어진다.
 */
export function pruneBroadcast(stillActive: (id: number) => boolean, cutNow = false): void {
  const dead = (it: BroadcastItem) => !!it.alertIds?.length && !it.alertIds.some((id) => stillActive(id));
  broadcastQueue = broadcastQueue.filter((it) => !dead(it));
  // ⛔ 읽는 중인 문장은 서버가 알려 온 해제(WS)일 때만 끊는다 — 30초 폴링 목록은 세 질의가 따로 도착해 잠깐
  //    빠질 수 있다(확인 직후 등). 그걸 해제로 보고 끊으면 아직 열린 경보를 덜 알린다(2026-10-06 검토).
  const cur = currentItem;
  if (cutNow && cur && !cur.cut && dead(cur)) cutCurrent(cur);
}

/** 서버가 알려 온 해제(WS `alert.cleared`) — 폴링(30초)을 기다리지 않고 그 경보의 낭독을 바로 거둔다. */
export function cancelBroadcastForAlerts(ids: number[]): void {
  for (const id of ids) clearedAlertIds.add(id);
  // 해제 id 는 다시 열리지 않으므로 오래된 것부터 버린다(세션이 길어도 커지지 않게).
  while (clearedAlertIds.size > 500) {
    const first = clearedAlertIds.values().next().value;
    if (first === undefined) break;
    clearedAlertIds.delete(first);
  }
  pruneBroadcast((id) => !clearedAlertIds.has(id), true);
}

/** 지금 읽는 한 문장만 멈춘다(웹 음성·서버 음원·네이티브 엔진). 큐와 에포크는 그대로 — 다음 항목이 이어진다. */
function cutCurrent(item: BroadcastItem): void {
  item.cut = true;
  // 그 항목을 읽는 엔진만 멈춘다. 음원을 받는 중(leg 없음)이면 멈출 것이 없다 — 도착 후 cut 을 보고 틀지 않는다.
  if (item.leg === "web") {
    try {
      if (supported) window.speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
  } else if (item.leg === "native") {
    nativeStopSpeaking();
  }
  const audio = item.leg === "audio" ? item.audio : undefined;
  if (audio) {
    try {
      audio.pause();
      // 일시정지만으로는 끝 이벤트가 오지 않는다 — 끝 처리기(cut 확인 후 정리만 한다)를 직접 깨운다.
      audio.dispatchEvent(new Event("ended"));
    } catch {
      /* ignore */
    }
  }
  const f = abortCurrent;
  abortCurrent = null;
  f?.();
}

/** Play one queued item, resolving when it finishes (or is abandoned by a stop).
 *  A watchdog guarantees the queue advances even if `onend`/`ended` never fire. */
function playBroadcastItem(item: BroadcastItem, epoch: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    // 안전망은 실제 발화보다 **길어야** 한다 — 예전 120ms/자는 일본어·인도네시아어 문장의 실제
    // 발화보다 짧아, 끝나기 전에 다음 언어가 시작돼 겹쳤다. 260ms/자 + 4초, 상한 60초.
    const guard = setTimeout(() => {
      item.stale = true;
      done();
    }, Math.min(60_000, Math.max(8_000, item.text.length * 260 + 4_000)));
    const finish = () => {
      clearTimeout(guard);
      if (abortCurrent === finish) abortCurrent = null;
      done();
    };
    abortCurrent = finish;
    if (epoch !== broadcastEpoch) return finish();

    /** 서버 edge-tts MP3 leg — 웹/네이티브 양쪽의 마지막 폴백(항상 finish 로 끝난다). */
    const serverLeg = async (): Promise<void> => {
      try {
        const res = await api.get("/tts/speak", {
          params: { text: item.text, lang: item.lang },
          responseType: "blob",
        });
        if (epoch !== broadcastEpoch || item.cut || item.stale) return finish();
        const url = URL.createObjectURL(res.data as Blob);
        const audio = new Audio(url);
        broadcastAudio = audio;
        item.audio = audio;
        item.leg = "audio";
        const cleanup = (ok: boolean, reason?: string) => {
          URL.revokeObjectURL(url);
          if (broadcastAudio === audio) broadcastAudio = null;
          if (item.cut) return finish();
          if (ok) noteSuccess(item.lang);
          else noteFailure(item.lang, reason ?? "audio_failed");
          finish();
        };
        audio.addEventListener("ended", () => cleanup(true), { once: true });
        audio.addEventListener("error", () => cleanup(false, "audio_error"), { once: true });
        await audio.play().catch((e: unknown) =>
          cleanup(false, `audio_play_failed:${errText(e)}`),
        );
      } catch (e) {
        if (!item.cut) noteFailure(item.lang, `server_tts_failed:${errText(e)}`);
        finish();
      }
    };
    /** serverLeg 과 같되 finish 를 부르지 않고 성공 여부만 돌려준다(서버 우선 경로용). */
    const serverSpeakItem = async (): Promise<boolean> => {
      try {
        const res = await api.get("/tts/speak", { params: { text: item.text, lang: item.lang }, responseType: "blob" });
        if (epoch !== broadcastEpoch) return true;
        if (item.cut || item.stale) return false;
        const url = URL.createObjectURL(res.data as Blob);
        const audio = new Audio(url);
        broadcastAudio = audio;
        item.audio = audio;
        item.leg = "audio";
        return await new Promise<boolean>((resolve) => {
          let done2 = false;
          const end = (ok: boolean, reason?: string) => {
            if (done2) return;
            done2 = true;
            URL.revokeObjectURL(url);
            if (broadcastAudio === audio) broadcastAudio = null;
            if (item.cut) return resolve(false);
            if (ok) noteSuccess(item.lang); else noteFailure(item.lang, reason ?? "audio_failed");
            resolve(ok);
          };
          audio.addEventListener("ended", () => end(true), { once: true });
          audio.addEventListener("error", () => end(false, "audio_error"), { once: true });
          audio.play().catch((e: unknown) => end(false, `audio_play_failed:${errText(e)}`));
        });
      } catch (e) {
        if (!item.cut) noteFailure(item.lang, `server_tts_failed:${errText(e)}`);
        return false;
      }
    };

    // ── 네이티브(Capacitor): OS TTS 가 1단계, 서버 edge-tts 가 2단계 ──────────
    // ⚠️ 완료를 await 해서 FIFO 큐가 겹치지 않게 한다(웹 경로는 아래 그대로).
    if (isNativeTTS()) {
      void (async () => {
        item.leg = "native";
        const r = await nativeSpeak(item.text, item.lang, () => !!item.cut || !!item.stale || epoch !== broadcastEpoch);
        if (epoch !== broadcastEpoch || item.cut || item.stale) return finish();
        if (r.status === "ok") {
          noteSuccess(item.lang);
          return finish();
        }
        // 엔진이 읽다 실패한 것은 기록한다. 언어팩이 없어 건너뛴 것(unsupported)은
        // 서버가 대신 읽으면 방송이 나간 것이므로 아직 실패가 아니다.
        if (r.status === "error") noteFailure(item.lang, `native_tts_failed:${r.reason}`);
        if (!item.serverFallback) {
          if (r.status === "unsupported") {
            noteFailure(item.lang, "no_local_voice_and_fallback_off");
          }
          return finish();
        }
        await serverLeg();
      })();
      return;
    }

    /** 브라우저 Web Speech leg — 시작했으면 true(끝나면 finish), 음성이 없거나 던지면 false. */
    const webSpeechLeg = (): boolean => {
      if (!supported) return false;
      const voice = pickVoice(item.lang);
      if (!voice && voiceCache.length > 0) return false;
      try {
        if (item.cut || item.stale) return false;
        item.leg = "web";
        const u = new SpeechSynthesisUtterance(item.text);
        u.lang = SPEECH_LOCALE[item.lang];
        if (voice) u.voice = voice;
        u.rate = 1;
        u.pitch = 1;
        u.onend = () => {
          if (!item.cut) noteSuccess(item.lang);
          finish();
        };
        u.onerror = (ev) => {
          const err = (ev as SpeechSynthesisErrorEvent).error ?? "unknown";
          // 정지/뮤트·해제로 끊긴 건 실패가 아니다(에포크 무효화와 같은 취급).
          if (!item.cut && err !== "interrupted" && err !== "canceled") {
            noteFailure(item.lang, `speech_error:${err}`);
          }
          finish();
        };
        window.speechSynthesis.speak(u);
        return true;
      } catch (e) {
        noteFailure(item.lang, `speech_throw:${errText(e)}`);
        return false;
      }
    };

    // 서버 신경망 음성 1순위(설정 기본) → 실패하면 브라우저 음성.
    if (item.serverFallback && useTtsStore.getState().preferServer) {
      void (async () => {
        const ok = await serverSpeakItem();
        if (epoch !== broadcastEpoch || item.cut || item.stale) return finish();
        if (ok) return finish();
        if (!webSpeechLeg()) finish();
      })();
      return;
    }
    if (webSpeechLeg()) return;
    // No local voice for this language → edge-tts fallback (awaited).
    if (!item.serverFallback) {
      noteFailure(item.lang, "no_local_voice_and_fallback_off");
      return finish();
    }
    void serverLeg();
  });
}

/** Drain the queue one item at a time. Idempotent: a second caller is a no-op
 *  while a drain is already running. */
async function drainBroadcast(): Promise<void> {
  if (broadcastPlaying) return;
  broadcastPlaying = true;
  const epoch = broadcastEpoch;
  while (broadcastQueue.length && epoch === broadcastEpoch) {
    const item = broadcastQueue.shift() as BroadcastItem;
    currentItem = item;
    await playBroadcastItem(item, epoch);
    if (currentItem === item) currentItem = null;
  }
  // Release only if still the current epoch; if a stop bumped it, whoever
  // started the next drain now owns the flag (prevents a double consumer).
  if (epoch === broadcastEpoch) broadcastPlaying = false;
}

/**
 * Enqueue an alert for broadcast in each selected language, in order. `buildText`
 * renders the localized phrase per language; empty selection falls back to ko.
 * Items join the global FIFO so a burst plays sequentially, never overlapping.
 */
function broadcastPhrases(
  buildText: (lang: AlertLang) => string,
  langs: AlertLang[],
  serverFallback: boolean,
  alertIds?: number[],
): void {
  const list = langs.length ? langs : (["ko"] as AlertLang[]);
  for (const lang of list) {
    broadcastQueue.push({ text: buildText(lang), lang, serverFallback, alertIds });
  }
  void drainBroadcast();
}

export interface UseAlertTTS {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  serverFallback: boolean;
  setServerFallback: (v: boolean) => void;
  /** 브라우저에서 서버 신경망 음성 우선(기본 켬). */
  preferServer: boolean;
  setPreferServer: (v: boolean) => void;
  langOverride: Language | null;
  setLangOverride: (v: Language | null) => void;
  /** Languages an automatic alarm is spoken in, in order (Settings-controlled). */
  broadcastLangs: AlertLang[];
  setBroadcastLangs: (v: AlertLang[]) => void;
  /** 서버(`voice_lang`)와 동기화되는 이 계정의 주 음성 언어. */
  voiceLang: AlertLang;
  setVoiceLang: (v: AlertLang) => void;
  /** 표시 언어 변경에 음성을 따르게 한다 — 규칙은 store 의 같은 이름 항목 주석. */
  followDisplayLanguage: (v: AlertLang) => void;
  /** 마지막 발화 실패(성공하면 해제) — 상단바 표시·진단용. */
  lastFailure: TtsFailure | null;
  /** Effective *display* voice language right now (override, else UI language). */
  lang: AlertLang;
  supported: boolean;
  /**
   * Imperatively speak an arbitrary string right now — used by the overlay's
   * "replay" button. Unlike `announce`, this does NOT gate on `enabled`
   * (an explicit user gesture always plays); it still honours the server
   * fallback + platform support.
   */
  speak: (text: string, lang?: AlertLang) => void;
  /** `speak` 과 동일하되 발화 종료까지 기다린다(순차 테스트용). */
  speakAndWait: (text: string, lang?: AlertLang) => Promise<void>;
  /**
   * 테스트 방송 — 실제 경보와 **같은 경로**(경보음 → 선택한 방송 언어 전부, 주 언어 먼저)로
   * 샘플 경보를 내보낸다. 명시 조작이라 `enabled` 게이트를 타지 않는다.
   */
  speakTest: (level?: number, gas?: string) => void;
  /**
   * Announce a batch of alerts. Only level >= 2 and not-yet-spoken alerts are
   * read aloud; everything else is silently marked as seen so a later level
   * bump on the same id still won't re-trigger. 주의(level 1)는 낭독 대신 짧은
   * 알림음 한 번(설정 `cautionChime`, 기본 끔 — 끄면 종전처럼 무음). 설비 경보(kind=health)는 알림음도 없다.
   */
  announce: (alerts: TtsAlertLike[], siteName?: (id: number | string) => string) => void;
  /**
   * Speak a single alert (used by the critical-alarm overlay). Shares the
   * dedupe set with `announce`, so it never double-speaks a passive one.
   */
  announceOne: (alert: TtsAlertLike, siteName?: (id: number | string) => string) => void;
  /**
   * 낭독했던 경보가 해제됐음을 **주 음성 언어로 한 번** 알린다(경보음 없음). 호출자가 「낭독한 경보가
   * 목록에서 사라졌다」를 판단해 부른다(AlertTtsAnnouncer). `enabled` 게이트를 탄다.
   */
  announceCleared: (alert: TtsAlertLike, siteName?: (id: number | string) => string) => void;
  /** Build the localized spoken phrase for an alert (no audio). */
  phrase: (level: number, gas?: string | null, site?: string | null) => string;
}

function clampLevel(n: number): Level {
  if (n >= 3) return 3;
  if (n === 2) return 2;
  if (n === 1) return 1;
  return 0;
}

export function useAlertTTS(): UseAlertTTS {
  const enabled = useTtsStore((s) => s.enabled);
  const setEnabled = useTtsStore((s) => s.setEnabled);
  const serverFallback = useTtsStore((s) => s.serverFallback);
  const setServerFallback = useTtsStore((s) => s.setServerFallback);
  const preferServer = useTtsStore((s) => s.preferServer);
  const setPreferServer = useTtsStore((s) => s.setPreferServer);
  const langOverride = useTtsStore((s) => s.langOverride);
  const setLangOverride = useTtsStore((s) => s.setLangOverride);
  const broadcastLangs = useTtsStore((s) => s.broadcastLangs);
  const setBroadcastLangs = useTtsStore((s) => s.setBroadcastLangs);
  const voiceLang = useTtsStore((s) => s.voiceLang);
  const setVoiceLangRaw = useTtsStore((s) => s.setVoiceLang);
  const followDisplayLanguage = useTtsStore((s) => s.followDisplayLanguage);
  const lastFailure = useTtsStore((s) => s.lastFailure);

  // Resolve the effective voice language: explicit override, else UI language.
  const lang: AlertLang = langOverride ?? toAlertLang(currentLanguage());

  // Keep latest values in refs so `announce`/`speak` stay referentially stable
  // (safe as effect deps) yet always read current settings.
  const ref = useRef({ enabled, serverFallback, lang, broadcastLangs, voiceLang });
  ref.current = { enabled, serverFallback, lang, broadcastLangs, voiceLang };

  const phrase = useCallback(
    (level: number, gas?: string | null, site?: string | null) =>
      buildAlertPhrase({ lang: ref.current.lang, level: clampLevel(level), gas, site }),
    [],
  );

  const speak = useCallback((text: string, spokenLang?: AlertLang) => {
    // Imperative (explicit gesture) — no `enabled` gate; see interface doc.
    const { serverFallback: sf, lang: l } = ref.current;
    speakText(text, spokenLang ?? l, sf);
  }, []);

  /**
   * `speak` 과 같지만 **발화가 끝날 때까지 기다린다**.
   * 여러 언어를 순서대로 들려주는 테스트(설정 > 8개국어 순차 재생)에 쓴다 —
   * 기다리지 않고 연달아 던지면 8개가 겹쳐 재생돼 무엇이 나온 건지 알 수 없다.
   */
  const speakAndWait = useCallback(async (text: string, spokenLang?: AlertLang) => {
    const { serverFallback: sf, lang: l } = ref.current;
    await speakTextAsync(text, spokenLang ?? l, sf);
  }, []);

  /**
   * 자동 경보 발화의 단일 입구(announce/announceOne 둘 다 여기로 온다).
   *
   * **경보음 → 말** 순서다(방송 관례). 경보음(`lib/alarmTone.ts`)은 푸시 채널과 같은
   * 소리이고, 음성이 꺼져 있어도 울린다 — 음성 끔은 「낭독」을 끄는 것이지 경보를
   * 끄는 게 아니다(끄는 곳: 설정 > 음성 경보 방송 > 경보음). 신호음이 실패해도
   * 약속은 resolve 되므로 말이 막히는 일은 없다.
   *
   * ⚠️ 이 순서를 호출자(오버레이·낭독기)에 맡기면 안 된다 — 둘이 같은 경보를 거의
   *    동시에 보므로 한쪽이 신호음을 기다리는 사이 다른 쪽이 먼저 말해 버린다
   *    (2026-09-11 실기: 사이렌과 음성이 겹쳐 나왔다). 그래서 여기서 묶는다.
   */
  const speakAlert = useCallback(
    (a: TtsAlertLike, siteName?: (id: number | string) => string) => {
      const level = clampLevel(a.level);
      // 이 낭독이 대신하는 경보 전부 — 묶음 중 하나만 해제됐다고 끊으면 아직 열린 경보를 덜 알린다(2026-10-01 검토).
      const ids = a.groupIds?.length ? a.groupIds : a.id != null ? [a.id] : undefined;
      const say = () => {
        const { enabled: on, serverFallback: sf, broadcastLangs: langs, voiceLang: primary } = ref.current;
        if (!on) return;
        // 경보음이 울리는 사이(약 3초) 묶음이 전부 해제됐다 — 낭독하지 않는다.
        if (ids?.length && ids.every((id) => clearedAlertIds.has(id))) return;
        const id = a.site ?? "";
        const site = siteName && id !== "" ? siteName(id) : undefined;
        // Broadcast the SAME alert in every operator-selected language — the
        // primary (= display language) first, the rest in canonical order. The
        // person holding the phone hears their own language before the others.
        const ordered = [primary, ...langs.filter((l) => l !== primary)] as AlertLang[];
        broadcastPhrases(
          (bl) =>
            buildSpokenAlert({
              lang: bl, level, gas: a.gas, site,
              kind: a.kind, isDrill: a.is_drill, subjectKey: a.subject_key,
            }),
          ordered,
          sf,
          ids,
        );
      };
      // 설비 무응답(health)은 가스 위험이 아니다 — 가스 사이렌(경보음)을 울리지 않고 안내 음성만 낸다.
      //   (2026-09-28: 「현장 PC 무응답」에 사이렌 + 「복합 가스 누출… 대피」가 나갔다.)
      if (a.kind !== "health" && useAlarmSettings.getState().sound) void playAlarmTone().then(say);
      else say();
    },
    [],
  );

  const speakTest = useCallback((level = 2, gas = "H2S") => {
    const { serverFallback: sf, broadcastLangs: langs, voiceLang: primary } = ref.current;
    const ordered = [primary, ...langs.filter((l) => l !== primary)] as AlertLang[];
    const say = () =>
      broadcastPhrases((bl) => buildAlertPhrase({ lang: bl, level: clampLevel(level), gas, site: null }), ordered, sf);
    if (useAlarmSettings.getState().sound) void playAlarmTone().then(say);
    else say();
  }, []);

  const announceOne = useCallback(
    (a: TtsAlertLike, siteName?: (id: number | string) => string) => {
      if (!a || a.id == null || spoken.has(a.id)) return;
      spoken.add(a.id);
      if (isSilentHealth(a.kind, a.subject_key)) return;   // 경광등 끊김 — 읽지 않는다(2026-11-04 「방송 안 함」)
      if (a.level < 2) {
        if (chimesFor(a)) chimeCaution();
        return;
      }
      speakAlert(a, siteName);
    },
    [speakAlert],
  );

  const announce = useCallback(
    (alerts: TtsAlertLike[], siteName?: (id: number | string) => string) => {
      const fresh = alerts.filter((a) => a && a.id != null && !spoken.has(a.id));
      let caution = false;
      for (const a of fresh) {
        spoken.add(a.id);
        if (isSilentHealth(a.kind, a.subject_key)) continue;   // 경광등 끊김 — 읽지 않는다
        if (a.level < 2) {
          if (chimesFor(a)) caution = true;
          continue;
        }
        speakAlert(a, siteName);
      }
      // 주의(1단계)는 낭독하지 않고 짧은 알림음만 — 한 번 부를 때 여러 건이어도 한 번(설정 기본 끔).
      if (caution) chimeCaution();
    },
    [speakAlert],
  );

  const announceCleared = useCallback(
    (a: TtsAlertLike, siteName?: (id: number | string) => string) => {
      const { enabled: on, serverFallback: sf, voiceLang: primary } = ref.current;
      if (!on || !a) return;
      const id = a.site ?? "";
      const site = siteName && id !== "" ? siteName(id) : undefined;
      broadcastPhrases(
        (bl) => buildClearedAlert({
          lang: bl, level: clampLevel(a.level), gas: a.gas, site,
          kind: a.kind, isDrill: a.is_drill, subjectKey: a.subject_key,
        }),
        [primary],
        sf,
      );
    },
    [],
  );

  // Stop any queued speech + fallback audio when the user turns TTS off.
  useEffect(() => {
    if (!enabled) {
      if (supported) {
        try {
          window.speechSynthesis?.cancel();
        } catch {
          /* ignore */
        }
      }
      stopFallbackAudio();
    }
  }, [enabled]);

  // Additionally halt an in-flight *multilingual* broadcast when voice is turned
  // off. The effect above cancels queued Web Speech; this stops the server-audio
  // leg the broadcaster owns. Kept as a separate effect so the single-shot
  // voice-stop/mute path stays untouched.
  useEffect(() => {
    if (!enabled) stopBroadcast();
  }, [enabled]);

  return {
    enabled,
    setEnabled,
    serverFallback,
    setServerFallback,
    preferServer,
    setPreferServer,
    langOverride,
    setLangOverride,
    broadcastLangs,
    setBroadcastLangs,
    voiceLang,
    setVoiceLang: setVoiceLangRaw,
    followDisplayLanguage,
    lastFailure,
    lang,
    // 네이티브에서는 Web Speech 유무와 무관하게 OS TTS·서버 폴백으로 소리가 난다.
    // (웹에서는 `isNativeTTS()` 가 false 라 기존 값 그대로다.)
    supported: supported || isNativeTTS(),
    speak,
    speakAndWait,
    speakTest,
    announce,
    announceOne,
    announceCleared,
    phrase,
  };
}


// ── 개인 음성 언어 서버 동기화 (FE-5) ─────────────────────────────────────────
// 방송 언어가 브라우저 localStorage 에만 있으면 단말을 바꾸는 순간 초기화된다.
// 백엔드가 사용자 프로필에 노출하는 `voice_lang`(주 언어 문자열 하나)과 store 를
// 양방향으로 잇되, **서버에 그 필드가 아직 없어도 조용히 무시**한다:
// 값이 없으면 기존 localStorage 설정을 그대로 쓰고, PATCH 실패는 삼킨다.
//
// `lastPushed` = 「서버가 알고 있다고 믿는 값」. 첫 렌더에서 로컬값으로 baseline 을
// 잡으므로 부팅만으로 서버에 쓰지 않고, 사용자가 바꾼 뒤에만 PATCH 가 나간다.
let lastPushed: AlertLang | null = null;

/** 서버 프로필에서 읽은 값을 AlertLang 으로 정규화. 인식 불가 → null. */
function parseVoiceLang(raw: unknown): AlertLang | null {
  if (typeof raw !== "string") return null;
  // 백엔드가 콤마 목록/BCP-47("ja-JP")을 줄 수도 있으니 첫 토큰의 기본 언어만 본다.
  const base = raw.trim().toLowerCase().split(",")[0]?.split("-")[0] ?? "";
  return (ALERT_LANGUAGES as readonly string[]).includes(base) ? (base as AlertLang) : null;
}

/**
 * 로그인/부팅 시 서버 `voice_lang` → store, 이후 사용자가 바꾸면 store → PATCH.
 * AppShell 에서 한 번만 마운트한다.
 */
export function useVoiceLangSync(): void {
  const user = useAuth((s) => s.user);
  const authed = useAuth((s) => !!s.access);
  const voiceLang = useTtsStore((s) => s.voiceLang);

  // 1) 서버 → store. 필드가 없거나 값이 이상하면 아무것도 하지 않는다.
  useEffect(() => {
    if (!user) return;
    const fromServer = parseVoiceLang((user as { voice_lang?: unknown }).voice_lang);
    if (!fromServer) return;
    lastPushed = fromServer;
    if (useTtsStore.getState().voiceLang !== fromServer) {
      useTtsStore.getState().setVoiceLang(fromServer);
    }
  }, [user]);

  // 2) store → 서버. 첫 실행은 baseline 만 잡고 쓰지 않는다.
  useEffect(() => {
    if (!authed) return;
    if (lastPushed === null) {
      lastPushed = voiceLang;
      return;
    }
    if (lastPushed === voiceLang) return;
    lastPushed = voiceLang;
    void authApi.updateMe({ voice_lang: voiceLang }).catch(() => {
      // 서버에 아직 `voice_lang` 이 없을 수 있다 → 조용히 무시하고 로컬 설정 유지.
    });
  }, [authed, voiceLang]);
}
